import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuColumns3, LuEye, LuRotateCcw } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import Popover from '../../../components/ui/Popover';
import { useTranslation } from '../../../i18n/LanguageContext';
import { COLUMN_KEYS, EXIT_DATE_COLUMN, columnLabel } from '../employeeColumns';

/** "Sütunlar" button: which table columns are shown. */
const ColumnMenu = ({ visibleColumns, exitDateAutoHidden, onToggle, onShowAll, onReset }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const visibleCount = COLUMN_KEYS.filter((key) => visibleColumns[key]).length;

  return (
    <>
      <Button
        ref={buttonRef}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('employees.columnsMenu.buttonLabel', { count: visibleCount, total: COLUMN_KEYS.length })}
      >
        <LuColumns3 aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-500 dark:tw-text-slate-400" />
        <span className="tw-hidden sm:tw-inline">{t('employees.columnsMenu.button')}</span>
        <span className="tw-rounded-full tw-bg-slate-900/[0.06] tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-tabular-nums tw-text-slate-500 dark:tw-bg-white/10 dark:tw-text-slate-300">
          {visibleCount}/{COLUMN_KEYS.length}
        </span>
      </Button>

      <Popover
        anchorRef={buttonRef}
        open={open}
        onClose={() => setOpen(false)}
        align="end"
        role="menu"
        label={t('employees.columnsMenu.title')}
        className="tw-w-[28rem] tw-p-2"
      >
        <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-3 tw-px-2.5 tw-pb-2 tw-pt-1.5">
          <p className="tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{t('employees.columnsMenu.title')}</p>
          <p className="tw-text-xs tw-font-medium tw-text-slate-500 tw-tabular-nums dark:tw-text-slate-400">
            {t('employees.columnsMenu.visible', { count: visibleCount, total: COLUMN_KEYS.length })}
          </p>
        </div>

        <div className="tw-grid tw-max-h-[min(22rem,60vh)] tw-gap-0.5 tw-overflow-y-auto tw-overscroll-contain glass-scrollbar sm:tw-grid-cols-2">
          {COLUMN_KEYS.map((key) => {
            const checked = Boolean(visibleColumns[key]);
            const isLastVisible = checked && visibleCount <= 1;
            const autoHidden = key === EXIT_DATE_COLUMN && exitDateAutoHidden && checked;
            return (
              <button
                key={key}
                type="button"
                role="menuitemcheckbox"
                aria-checked={checked}
                disabled={isLastVisible}
                title={isLastVisible ? t('employees.columnsMenu.lastColumn') : undefined}
                onClick={() => onToggle(key)}
                className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-xl tw-bg-transparent tw-px-2.5 tw-py-2 tw-text-left tw-text-sm tw-font-medium tw-text-slate-700 tw-transition-colors hover:tw-bg-slate-900/[0.05] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 dark:tw-text-slate-200 dark:hover:tw-bg-white/[0.07]"
              >
                <span
                  aria-hidden="true"
                  className={`tw-grid tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-place-items-center tw-rounded-md tw-border tw-transition-colors ${
                    checked
                      ? 'tw-border-teal-500 tw-bg-teal-500 tw-text-white dark:tw-border-teal-400 dark:tw-bg-teal-500'
                      : 'tw-border-slate-300 tw-bg-white/60 dark:tw-border-white/20 dark:tw-bg-white/[0.04]'
                  }`}
                >
                  {checked && <LuCheck className="tw-h-3 tw-w-3" strokeWidth={3} />}
                </span>
                <span className="tw-min-w-0 tw-flex-1 tw-truncate">{columnLabel(t, key)}</span>
                {autoHidden && (
                  <span
                    title={t('employees.columnsMenu.hiddenWhenActiveHint')}
                    className="tw-shrink-0 tw-rounded-full tw-bg-slate-900/[0.06] tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-text-slate-500 dark:tw-bg-white/10 dark:tw-text-slate-400"
                  >
                    {t('employees.columnsMenu.hiddenWhenActive')}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="tw-mt-2 tw-flex tw-items-center tw-justify-end tw-gap-1 tw-border-t tw-border-slate-200/70 tw-px-1 tw-pt-2 dark:tw-border-white/10">
          <Button variant="ghost" size="sm" onClick={onShowAll}>
            <LuEye aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
            {t('employees.columnsMenu.showAll')}
          </Button>
          <Button variant="ghost" size="sm" onClick={onReset} title={t('employees.columnsMenu.resetHint')}>
            <LuRotateCcw aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
            {t('employees.columnsMenu.reset')}
          </Button>
        </div>
      </Popover>
    </>
  );
};

ColumnMenu.propTypes = {
  visibleColumns: PropTypes.objectOf(PropTypes.bool).isRequired,
  exitDateAutoHidden: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onShowAll: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

export default ColumnMenu;
